import React, { createContext, FC, useEffect, useMemo, useState } from "react";

import { getApiUrl } from "@/config";
import InteractionService from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import {
  ApiResponse,
  QuizVote,
  QuizVoteResponse,
  Slide,
} from "@/service/Model";
import TrackingService from "@/service/Tracking";

export type QuizProviderProps = {
  quizId: string;
  slides: Slide[];
  preview: boolean;
  children?: React.ReactNode;
};

export type QuizContextProps = {
  votes: QuizVote[];
  isPreview: boolean;
  correctAnswersCount: number;
  hasAllAnswersCorrect: boolean;
  onVote: (vote: QuizVote) => Promise<void>;
  hasVotes: boolean;
  resetVotes: () => void;
  getCorrectAnswers: () => number;
  isSlideVoted: (slideId: string) => boolean;
  isQuizVoted: () => boolean;
};

const QuizContext = createContext<QuizContextProps>({
  votes: [],
  isPreview: false,
  correctAnswersCount: 0,
  hasAllAnswersCorrect: false,
  onVote: async () => undefined,
  hasVotes: false,
  resetVotes: () => undefined,
  getCorrectAnswers: () => 0,
  isSlideVoted: () => false,
  isQuizVoted: () => false,
});

const QuizProvider: FC<QuizProviderProps> = ({
  quizId,
  slides,
  preview,
  children,
}) => {
  const [votes, setVotes] = useState<QuizVote[]>(
    preview ? [] : (LocalStorageService.getVotes(quizId) as QuizVote[]) || []
  );
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [hasAllAnswersCorrect, setHasAllAnswersCorrect] = useState(false);

  const api = useMemo(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  useEffect(() => {
    if (!preview && quizId && votes.length) {
      fetchCorrectAnswers(votes).then((count) => {
        setCorrectAnswersCount(count);
        setHasAllAnswersCorrect(count === votes.length);
      });
    }
  }, []);

  const fetchCorrectAnswers = async (
    votesToCheck: QuizVote[]
  ): Promise<number> => {
    const response: ApiResponse<QuizVoteResponse> =
      await api.getQuizCorrectAnswersCount(quizId, votesToCheck);
    return response.data?.correctAnswerCount || 0;
  };

  const onVote = async (vote: QuizVote): Promise<void> => {
    const response = await api.getQuizCorrectAnswersCount(quizId, [vote]);
    if (!response.data) return;

    vote.answer = response.data.correctAnswerCount === 1;
    vote.correctAnswers = response.data.correctAnswers || [];
    const updatedVotes = [...votes, vote];

    if (!preview) {
      sendVote(vote);
      LocalStorageService.setVotes(quizId, updatedVotes);
      TrackingService.getInstance().trackOnVote();
    }

    setVotes(updatedVotes);
    setCorrectAnswersCount(
      correctAnswersCount + response.data.correctAnswerCount
    );
    setHasAllAnswersCorrect(
      updatedVotes.length ===
        correctAnswersCount + response.data.correctAnswerCount
    );
  };

  const hasVotes = votes.length > 0;

  const resetVotes = () => {
    if (!preview) LocalStorageService.setVotes(quizId, undefined);
    setVotes([]);
    setCorrectAnswersCount(0);
    setHasAllAnswersCorrect(false);
  };

  const isSlideVoted = (slideId: string) =>
    votes.some((vote) => vote.slideId === slideId);

  const isQuizVoted = () =>
    slides.every((slide) => votes.some((vote) => vote.slideId === slide.id));

  const sendVote = (vote: QuizVote) => {
    api.postQuizResults(vote.quizId, vote.slideId, vote.optionId, !hasVotes);
  };

  const value = useMemo(
    () => ({
      votes,
      isPreview: preview,
      correctAnswersCount,
      hasAllAnswersCorrect,
      onVote,
      hasVotes,
      resetVotes,
      getCorrectAnswers: () => correctAnswersCount,
      isSlideVoted,
      isQuizVoted,
    }),
    [votes, preview, correctAnswersCount, hasAllAnswersCorrect]
  );

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
};

const QuizConsumer = QuizContext.Consumer;

export { QuizConsumer, QuizProvider };
export default QuizContext;
